import { DotLottie, Layout } from "@lottiefiles/dotlottie-web";
import React from "react";

import { getWindowOrigin } from "./utils";

// Types
export interface LottieConfig {
  animationPath: string;
  canvasId: string;
  dimensions: {
    width?: number;
    height: number;
    minHeight?: number;
  };
  options?: {
    autoplay?: boolean;
    loop?: boolean;
    layout?: {
      fit?: "cover" | "contain" | "fill" | "fit-width" | "fit-height" | "none";
      align?: [number, number];
    };
  };
}

export interface LottieInstance {
  dotLottie: DotLottie;
  canvas: HTMLCanvasElement;
  destroy: () => void;
}

/**
 * Load a Lottie animation into a canvas.
 * Provides error handling, safe cleanup, and predictable layout.
 */
export const loadLottieAnimation = (
  config: LottieConfig,
): LottieInstance | null => {
  try {
    const { canvasId, animationPath, dimensions, options } = config;

    const canvas = document.getElementById(canvasId);
    if (!(canvas instanceof HTMLCanvasElement)) {
      console.error(
        `Canvas element with id "${canvasId}" not found or is not a <canvas>.`,
      );
      return null;
    }

    // Apply dimensions safely
    if (dimensions.width) {
      canvas.width = dimensions.width;
      canvas.style.width = `${dimensions.width}px`;
    }

    canvas.height = dimensions.height;
    canvas.style.height = `${dimensions.height}px`;

    if (dimensions.minHeight) {
      canvas.style.minHeight = `${dimensions.minHeight}px`;
    }

    // Build animation URL
    const animationSrc = `${getWindowOrigin()}/web2-assets/${animationPath}`;

    // Merge user options with defaults
    const autoplay = options?.autoplay ?? true;
    const loop = options?.loop ?? true;

    const layout: Layout = {
      fit: config.options?.layout?.fit ?? "cover", // always defined
      align: config.options?.layout?.align ?? [0.5, 0.5], // always defined
    };

    const dotLottie = new DotLottie({
      autoplay,
      loop,
      canvas,
      src: animationSrc,
      layout,
    });

    const destroy = () => {
      try {
        dotLottie.destroy();
      } catch (err) {
        console.error("Error destroying Lottie instance:", err);
      }
    };

    return { dotLottie, canvas, destroy };
  } catch (err) {
    console.error("Failed to load Lottie animation:", err);
    return null;
  }
};

/**
 * React hook for Lottie animations.
 * Automatically loads animation and performs cleanup on unmount.
 */
export const useLottieAnimation = (config: LottieConfig) => {
  const [instance, setInstance] = React.useState<LottieInstance | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    let isMounted = true;

    const init = () => {
      setIsLoading(true);
      setError(null);

      const created = loadLottieAnimation(config);

      if (isMounted) {
        setInstance(created);
        setIsLoading(false);

        if (!created) {
          setError(new Error("Lottie failed to initialise."));
        }
      }
    };

    init();

    return () => {
      isMounted = false;
      instance?.destroy();
    };
    // We do NOT include "instance" as a dependency to avoid double-execution
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  return { instance, isLoading, error };
};
